import { TableCell, TableRow, Tooltip } from "@mui/material";
import React, { useState } from "react";
import { Share } from "./Share";
import { ReactComponent as ArrowFromSquare } from "@assets/images/icons/arrow-from-square.svg";
import PartialCloseIcon from "@assets/icons/PartialCloseIcon";
import { useTranslation } from "@helpers/translate";
import { getPositionPnlRoi } from "@helpers/formatOrders";
import { TradeCell } from "./TradeCell";
import {
  formatDollarPrice,
  formatSignedPnl,
  formatTpSlAmount,
  formatWager,
  getTriggerPnl,
  toNumber,
} from "./positionFormat";
import { isNestedControlClick } from "./rowClick";
import "./index.sass";

export const PositionItem = ({
  order,
  onCloseClick,
  onPartialCloseClick,
  onRowClick,
  markPrice,
  onPairClick,
}) => {
  const { __ } = useTranslation();
  const [share, setShare] = useState(false);
  const { pnlText, pnlValue, isSellSide } = getPositionPnlRoi(order, markPrice);

  const entryPrice =
    toNumber(order?.entry_price ?? order?.fill_price ?? order?.price) ?? 0;
  const quantity = Math.abs(toNumber(order?.quantity ?? order?.qty) ?? 0);
  const leverage = toNumber(order?.leverage);
  const livePnl =
    pnlValue != null && Number.isFinite(pnlValue)
      ? pnlValue
      : toNumber(order?.pnl);
  const pnlClass =
    livePnl == null || livePnl === 0
      ? ""
      : livePnl > 0
        ? "trade-table__pnl--positive"
        : "trade-table__pnl--negative";

  const takeProfit = formatTpSlAmount(
    getTriggerPnl(
      order?.take_profit_trigger_price,
      entryPrice,
      quantity,
      isSellSide,
    ),
  );
  const stopLoss = formatTpSlAmount(
    getTriggerPnl(
      order?.stop_loss_trigger_price,
      entryPrice,
      quantity,
      isSellSide,
    ),
  );

  // Like a tap on a mobile card: the row opens the trade details,
  // its own buttons (pair, share, partial close, close) keep their actions
  const handleRowClick = (event) => {
    if (isNestedControlClick(event)) return;

    onRowClick(order);
  };

  return (
    <>
      <TableRow
        className="trade-table__row--clickable"
        onClick={handleRowClick}
      >
        <TableCell className="trade-table__cell trade-table__cell--pair">
          <TradeCell
            symbol={order.symbol}
            isLong={!isSellSide}
            onPairClick={onPairClick}
            iconSize={16}
            longColor="#2FA766"
            shortColor="#E2464A"
          />
        </TableCell>

        <TableCell className="trade-table__cell trade-table__cell--data">
          {formatWager(order)}
        </TableCell>

        <TableCell className="trade-table__cell trade-table__cell--data">
          {leverage != null ? `${leverage}x` : "—"}
        </TableCell>

        <TableCell className="trade-table__cell trade-table__cell--data">
          {formatDollarPrice(
            order?.entry_price ?? order?.fill_price ?? order?.price,
          )}
        </TableCell>

        <TableCell className="trade-table__cell trade-table__cell--data">
          {formatDollarPrice(order?.liq_price)}
        </TableCell>

        <TableCell
          className={`trade-table__cell trade-table__cell--pnl ${pnlClass}`}
        >
          <div className={`trade-table__pnl ${pnlClass}`}>
            <span className="trade-table__pnl-value">
              {pnlText === "--" && livePnl == null
                ? "—"
                : formatSignedPnl(livePnl)}
            </span>
            <button
              onClick={() => setShare(true)}
              type="button"
              className="trade-table__share-btn"
              aria-label={__("futures.share")}
            >
              <ArrowFromSquare />
            </button>
          </div>
        </TableCell>

        <TableCell
          className="trade-table__cell trade-table__cell--tpsl"
          data-futures-guide="manage-position-tp-sl"
        >
          <div className="trade-table__tpsl">
            {takeProfit || stopLoss ? (
              <>
                <span className="trade-table__tpsl-side trade-table__tpsl-side--tp">
                  {takeProfit && (
                    <span className="trade-table__tp">{takeProfit}</span>
                  )}
                </span>
                <span className="trade-table__tpsl-divider" />
                <span className="trade-table__tpsl-side trade-table__tpsl-side--sl">
                  {stopLoss && (
                    <span className="trade-table__sl">{stopLoss}</span>
                  )}
                </span>
              </>
            ) : (
              <span className="trade-table__tpsl-empty">—</span>
            )}
          </div>
        </TableCell>

        <TableCell
          className="trade-table__cell trade-table__cell--actions"
          data-futures-guide="manage-position-close"
        >
          <div className="trade-table__actions">
            <Tooltip
              title={__("futures.partial_close")}
              placement="top"
              arrow
              classes={{
                tooltip: "trade-table__partial-tooltip",
                arrow: "trade-table__partial-tooltip-arrow",
              }}
            >
              <button
                type="button"
                className="trade-table__icon-btn"
                aria-label={__("futures.partial_close")}
                onClick={() => onPartialCloseClick(order)}
              >
                <PartialCloseIcon />
              </button>
            </Tooltip>
            <button
              onClick={() => onCloseClick(order)}
              type="button"
              className="trade-table__close-btn"
            >
              {__("futures.close")}
            </button>
          </div>
        </TableCell>
      </TableRow>

      <Share
        active={share}
        setActive={setShare}
        order={order}
        pnl={pnlText}
        markPrice={markPrice}
      />
    </>
  );
};
